import { useMemo } from "react"

import { useLocation, useMatch, useNavigate } from "react-router-dom"
import urlJoin from "url-join"

import { API_MODULE_DOCUMENT, APP_DOCUMENT, MODULE_DOCUMENTS } from "@l2r-front/l2r-app-doc"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { useMapDispatchContext } from "@l2r-front/l2r-map"
import { useGetAvailableSubnetworkForModule, useNetworksStateContext } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { API_MODULE_CAMPAIGN, APP_CAMPAIGN, MODULE_CAMPAIGNS } from "@l2r-front/l2r-shared-campaign"
import { BottomSheetHeights, useApplicationDispatchContext } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../common/constants/i18n"
import { useGetActiveModules } from "../../hooks/useGetActiveModules"
import { cleanInnerPath } from "../../utils/urlUtils"

import * as Styled from "./MobileModulesSelector.styled"

export const MobileModulesSelector = (props) => {

    const {
        onClose,
    } = props

    const { t } = useTranslation()
    const { selectedNetwork } = useNetworksStateContext()
    const { storeMapBoundingBox } = useMapDispatchContext()
    const match = useMatch("/:selectedNetwork/:app/:module/*")
    const selectedModule = match?.params?.module
    const selectedApp = match?.params?.app
    const navigate = useNavigate()
    const location = useLocation()
    const appsWithActiveModules = useGetActiveModules()
    const { setCurrentBottomSheetHeight } = useApplicationDispatchContext()
    const availableSubNetworksForDocument = useGetAvailableSubnetworkForModule(API_MODULE_DOCUMENT)?.length > 0
    const availableSubNetworksForCampaign = useGetAvailableSubnetworkForModule(API_MODULE_CAMPAIGN)?.length > 0
    const hasSubNetworks = selectedNetwork.children?.length > 0

    const generalButtonsConfig = useMemo(() => [{
        id: API_MODULE_CAMPAIGN,
        label: t("l2r-app-campaign", "app.modules.campaigns"),
        moduleAvailableForSubNetworks: availableSubNetworksForCampaign,
        moduleApiKey: API_MODULE_CAMPAIGN,
        value: `/${APP_CAMPAIGN}/${MODULE_CAMPAIGNS}`,
    }, {
        id: API_MODULE_DOCUMENT,
        label: t("l2r-app-doc", "app.modules.documents"),
        moduleAvailableForSubNetworks: availableSubNetworksForDocument,
        moduleApiKey: API_MODULE_DOCUMENT,
        value: `/${APP_DOCUMENT}/${MODULE_DOCUMENTS}`,
    }], [availableSubNetworksForCampaign, availableSubNetworksForDocument, t])

    const modules = useMemo(() => {
        const sortedApps = Object.values(appsWithActiveModules).sort((a, b) => a.index - b.index).filter(app => app.path !== APP_DOCUMENT && app.path !== APP_CAMPAIGN)
        const appsToDisplay = sortedApps.map(app => {
            const sortedModules = Object.values(app.modules).sort((a, b) => a.index - b.index)
            const appModules = sortedModules.map(module => {
                const modulePath = urlJoin("/", app.path ?? "", module.path)
                return ({
                    id: module.path,
                    label: t(app.i18nNamespace, module.label),
                    disabled: module.disabled,
                    value: modulePath,
                })
            })
            const apps = [
                app.label && <Styled.ListSubheader
                    key={t(app.i18nNamespace, app.label)}
                    variant="Desktop & Mobile/Body/Regular"
                >
                    {app.icon({ color: "primary" })}
                    <Styled.AppTitle variant="Mobile/Body/Bold">
                        {t(app.i18nNamespace, app.label)}
                    </Styled.AppTitle>
                </Styled.ListSubheader>,
                ...appModules,
            ]

            return apps
        })
        const displayGeneral = generalButtonsConfig.some(config => {
            return hasSubNetworks ? config.moduleAvailableForSubNetworks
                : selectedNetwork?.modules?.includes(config.moduleApiKey)
        })
        if (displayGeneral) {
            return appsToDisplay.concat([<Styled.ListSubheader
                key="general"
                variant="Desktop & Mobile/Body/Regular"
            >
                <Styled.AppTitle variant="Mobile/Body/Bold" >
                    {t(I18N_NAMESPACE, "containers.moduleSelector.generalAppLabel")}
                </Styled.AppTitle >
            </Styled.ListSubheader>,
            generalButtonsConfig.filter(config => {
                return hasSubNetworks ? config.moduleAvailableForSubNetworks
                    : selectedNetwork?.modules?.includes(config.moduleApiKey)
            }).map(config => {
                return {
                    id: config.moduleApiKey,
                    label: config.label,
                    value: config.value,
                }
            })])
        } else {
            return appsToDisplay
        }
    }, [appsWithActiveModules, generalButtonsConfig, hasSubNetworks, selectedNetwork, t])

    if (!modules?.length) {
        return <Styled.Text variant="Desktop & Mobile/Body/Regular">
            {t(I18N_NAMESPACE, "containers.moduleSelector.emptyList")}
        </Styled.Text>
    }

    return <Styled.RadioButtonList
        id="modules-list-container"
        itemsList={modules?.flat()}
        onChange={(value) => {
            const previousPath = match?.params?.["*"].length ? match?.params?.["*"].replace(/code\/.*/, "") : ""
            const cleanedPreviousPath = cleanInnerPath(previousPath, match?.params?.app)
            storeMapBoundingBox()
            navigate(`/${selectedNetwork.slug}${value}/${cleanedPreviousPath}${location?.search}`)
            setCurrentBottomSheetHeight(BottomSheetHeights.SMALL)
            onClose()
        }}
        selectedValue={`/${selectedApp}/${selectedModule}`}
    />
}

MobileModulesSelector.propTypes = {
    onClose: PropTypes.func.isRequired,
}
